/**
 * PEStart — Read-only Adapter auf Live-Calc + Privatkostenkompass.
 * Kein Schreiben in appStore / peProfit / clientData.
 */

import type { PrivateCostState } from '../../store/types';
import {
    computeLoanAnnuitaet,
    createDefaultLoanPlan,
    DEFAULT_ANLAUF_PUFFER_MONATE,
    getInvestmentSums,
    type FoundingType,
    type InvestmentsState,
    type PEStartState,
    type StaffFlagsMap,
    type Subsidy,
} from '../../store/usePEStartStore';

export type PEStartLiveCalc = {
    sollumsatz: number;
    gesamtlohn: number;
    planumsatzGesamtNetto: number;
    monatsumsatzNetto: number;
    auslastungsfaktor: number;
    mwstSatz: number;
    currencyCode: string;
    monatsstundenChef: number;
    formatCurrency?: (value: number, decimals?: number) => string;
    personalData?: Array<{ id?: string; name?: string; totalCost?: number; monatsstunden?: number }>;
    unproductivePersonalData?: Array<{ id?: string; name?: string; totalCost?: number; monatsstunden?: number }>;
    azubiAssistenzData?: Array<{ id?: string; name?: string; totalCost?: number; monatsstunden?: number }>;
    produktiveLohnkostenMitarbeiter?: number;
    unproduktiveLohnkostenMitarbeiter?: number;
    azubiKostenGesamt?: number;
};

export type PEStartMonthRow = {
    month: number;
    umsatzZiel: number;
    umsatzPrognose: number;
    anlaufAuslastung: number;
    /** Anteil der Planungsanalyse-Verbesserung, der in diesem Monat greift (brutto) */
    verbesserungAnteilBrutto: number;
    fixkosten: number;
    personalkosten: number;
    unternehmerlohn: number;
    foerderung: number;
    investition: number;
    /** Kreditauszahlung (typ. Monat 1 = Netto-Finanzbedarf) */
    kreditAuszahlung: number;
    /** Annuitätenrate (Zins + Tilgung) */
    kreditrate: number;
    liquiditaetssaldo: number;
    decktTotpunkt: boolean;
};

export type PEStartPrognoseModus =
    | 'NEW_ANLAUF'
    | 'TAKEOVER_PLANZIEL'
    | 'TAKEOVER_PLANZIEL_PLUS_VERBESSERUNG';

export type PEStartDashboardModel = {
    foundingType: FoundingType;
    prognoseModus: PEStartPrognoseModus;
    /** Netto-Umsatzdelta aus Planungsanalyse (0 wenn keine) */
    verbesserungUmsatzDeltaNetto: number;
    kpis: {
        /** Kostendeckung netto (ohne Rücklagen-Aufschlag) */
        totpunktKostendeckungNetto: number;
        totpunktKostendeckungBrutto: number;
        /** Umsatzziel inkl. Rücklagen (Liquidität aus Settings, typ. +10%) */
        totpunktMonatNetto: number;
        totpunktMonatBrutto: number;
        ruecklagenFaktor: number;
        unternehmerlohnKompass: number;
        unternehmerlohnKompassRounded: number;
        unternehmerlohnSource: 'kompass' | 'pauschale';
        planumsatzNetto: number;
        /** Mindestumsatz pro Std. brutto bei Auslastungsziel — analog Gesamtleistung Rücklagen */
        stundensatzErforderlich: number;
        mindestUmsatzBrutto75: number;
        auslastungsziel: number;
        startFinanzbedarf: number;
        investitionenGesamt: number;
        foerderungenGesamt: number;
        monatsstundenGesamt: number;
        monatsstundenSalon: number;
        finanzbedarf: {
            investitionen: number;
            anlaufLiquiditaetspuffer: number;
            anlaufPufferMonate: number;
            foerderungen: number;
            nettoFinanzbedarf: number;
            /** = Kreditbetrag / Annuitäten-Schuld */
            kreditbetrag: number;
            tilgungLaufzeitJahre: number;
            zinssatzProzent: number;
            /** Konstante Annuitätenrate / Monat */
            rateMonat: number;
            /** @deprecated Alias für rateMonat (Kompatibilität) */
            tilgungMonat: number;
            zinsAnteilMonat1: number;
            tilgungAnteilMonat1: number;
            gesamtzahlung: number;
            gesamtzins: number;
        };
    };
    months: PEStartMonthRow[];
    breakEvenMonat: number | null;
    currencyCode: string;
    mwstSatz: number;
    formatCurrency: (value: number, decimals?: number) => string;
};

function n(v: unknown): number {
    const x = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(x) ? x : 0;
}

/** Privatkostenkompass: totalNeeded / totalNeededRounded (wie PrivatkostenKompassPage). */
export function computePrivateCostNeed(privateCostData?: PrivateCostState | null) {
    const cats = privateCostData?.monthlyCats || [];
    let living = 0;
    let social = 0;
    for (const cat of cats) {
        const catSum = (cat.items || []).reduce((s, i) => s + n(i.value), 0);
        if (cat.type === 'social') social += catSum;
        else living += catSum;
    }
    const periodic = (privateCostData?.periodicItems || []).reduce((acc, item) => {
        return acc + n(item.value) / (n(item.frequency) || 12);
    }, 0);
    const totalNeeded = living + periodic + social;
    const totalNeededRounded = Math.ceil(totalNeeded / 50) * 50;
    const hasKompassEntries =
        cats.some((c) => (c.items || []).some((i) => n(i.value) > 0)) ||
        (privateCostData?.periodicItems || []).some((i) => n(i.value) > 0);
    return {
        sumLivingMonthly: living,
        sumSocialMonthly: social,
        sumPeriodicMonthlyShare: periodic,
        totalNeeded,
        totalNeededRounded,
        hasKompassEntries,
    };
}

/**
 * Unternehmerbedarf: Kompass wenn gefüllt, sonst pauschale Planung
 * (unternehmerData.entnahme + sozialkosten / calc.gesamtlohn).
 */
export function resolveUnternehmerBedarf(
    privateCostData?: PrivateCostState | null,
    pauschaleBedarf?: number
) {
    const kompass = computePrivateCostNeed(privateCostData);
    const pauschale = Math.max(0, n(pauschaleBedarf));
    if (kompass.hasKompassEntries && kompass.totalNeeded > 0) {
        return {
            bedarf: kompass.totalNeeded,
            bedarfRounded:
                kompass.totalNeededRounded > 0
                    ? kompass.totalNeededRounded
                    : Math.ceil(kompass.totalNeeded / 50) * 50,
            source: 'kompass' as const,
            kompass,
            pauschale,
        };
    }
    return {
        bedarf: pauschale,
        bedarfRounded: pauschale > 0 ? Math.ceil(pauschale / 50) * 50 : 0,
        source: 'pauschale' as const,
        kompass,
        pauschale,
    };
}

export type StaffMemberRef = {
    id: string;
    name: string;
    totalCost: number;
    monatsstunden: number;
    pool: 'produktiv' | 'unproduktiv' | 'azubi';
};

export function collectStaffFromCalc(calc: PEStartLiveCalc): StaffMemberRef[] {
    const mapPool = (
        rows: PEStartLiveCalc['personalData'],
        pool: StaffMemberRef['pool']
    ): StaffMemberRef[] =>
        (rows || [])
            .filter((ma) => ma && (ma.id || ma.name))
            .map((ma, idx) => ({
                id: String(ma.id || `${pool}_${idx}`),
                name: String(ma.name || 'Unbenannt'),
                totalCost: n(ma.totalCost),
                monatsstunden: n(ma.monatsstunden),
                pool,
            }));

    return [
        ...mapPool(calc.personalData, 'produktiv'),
        ...mapPool(calc.unproductivePersonalData, 'unproduktiv'),
        ...mapPool(calc.azubiAssistenzData, 'azubi'),
    ];
}

function totalInvestments(foundingType: FoundingType, investments: InvestmentsState): number {
    const sums = getInvestmentSums(investments);
    return foundingType === 'NEW' ? sums.newTotal : sums.takeoverTotal;
}

function totalSubsidies(subsidies: Subsidy[]): number {
    return (subsidies || []).reduce((s, row) => s + n(row.amount), 0);
}

function subsidyForMonth(subsidies: Subsidy[], month: number): number {
    let sum = 0;
    for (const row of subsidies || []) {
        const covered = Math.min(6, Math.max(1, Math.round(n(row.monthsCovered) || 1)));
        if (month <= covered) {
            sum += n(row.amount) / covered;
        }
    }
    return sum;
}

function resolveFlag(
    flags: StaffFlagsMap,
    employeeId: string,
    foundingType: FoundingType
): { fromTakeover: boolean; startMonth: number } {
    const f = flags?.[employeeId];
    return {
        fromTakeover: f ? !!f.fromTakeover : foundingType === 'TAKEOVER',
        startMonth: f ? Math.min(6, Math.max(1, Math.round(n(f.startMonth) || 1))) : 1,
    };
}

function defaultFormatCurrency(currencyCode: string) {
    return (v: number, d = 0) =>
        new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currencyCode || 'EUR',
            minimumFractionDigits: d,
            maximumFractionDigits: d,
        }).format(v || 0);
}

const ANLAUF_PUFFER_MONATE_FALLBACK = DEFAULT_ANLAUF_PUFFER_MONATE;

function safeDiv(a: number, b: number): number {
    return b !== 0 ? a / b : 0;
}

/** Salon-Monatsstunden wie Gesamtleistung: Σ geoeffneteStunden × Wochensatz */
function resolveSalonMonatsStunden(calc: any): number {
    const wochensatz =
        n(calc?.wochensatz) || n(calc?.settings?.global?.Wochensatz) || 4.33;
    const geo =
        calc?.clientData?.unternehmerData?.geoeffneteStunden ||
        calc?.unternehmerData?.geoeffneteStunden ||
        [];
    if (Array.isArray(geo) && geo.length > 0) {
        const weekHrs = geo.reduce((s: number, h: unknown) => s + n(h), 0);
        if (weekHrs > 0) return Math.max(1, weekHrs * wochensatz);
    }
    return Math.max(1, n(calc?.monatsstundenChef) || 1);
}

/** Rücklagenfaktor analog Gesamtleistung: settings.global.Liquiditaet / 100 */
function resolveRuecklagenFaktor(calc: any): number {
    const fromSettings = calc?.settings?.global?.Liquiditaet;
    if (fromSettings !== undefined && fromSettings !== null && fromSettings !== '') {
        return n(fromSettings) / 100;
    }
    if (calc?.liquiditaet !== undefined && calc?.liquiditaet !== null) {
        const v = n(calc.liquiditaet);
        return v > 1 ? v / 100 : v;
    }
    return 0.1;
}

/**
 * Anlaufphase auf Basis Live-Calc + Privatkostenkompass + Gründungs-Extras.
 * Kennzahlen analog Gesamtleistungsseite (Rücklagen, Std. brutto @ Auslastung).
 *
 * @param verbesserungUmsatzDeltaNetto  Monatliches Umsatz-Delta aus Planungsanalyse
 *   (Prognose − Basis). Bei Übernahme: ab Planziel, Delta moderat über M1–M6.
 */
export function computePEStartDashboard(
    state: PEStartState,
    calc: PEStartLiveCalc,
    privateCostData?: PrivateCostState | null,
    verbesserungUmsatzDeltaNetto = 0
): PEStartDashboardModel {
    const ul = resolveUnternehmerBedarf(privateCostData, n(calc.gesamtlohn));
    const unternehmerlohn = ul.bedarf;
    const unternehmerlohnRounded = ul.bedarfRounded;

    const mwst = n(calc.mwstSatz);
    const mwstFaktor = 1 + mwst / 100;
    const currencyCode = calc.currencyCode || 'EUR';
    const formatCurrency =
        typeof calc.formatCurrency === 'function'
            ? calc.formatCurrency
            : defaultFormatCurrency(currencyCode);

    const baseSoll = n(calc.sollumsatz);
    const embeddedUl = n(calc.gesamtlohn);

    const staff = collectStaffFromCalc(calc);
    const fullPersonalFromRows = staff.reduce((s, m) => s + m.totalCost, 0);
    const fullPersonal =
        n(calc.produktiveLohnkostenMitarbeiter) +
            n(calc.unproduktiveLohnkostenMitarbeiter) +
            n(calc.azubiKostenGesamt) || fullPersonalFromRows;

    const fixSockel = Math.max(0, baseSoll - embeddedUl - fullPersonal);

    const hoursFromStaff = staff.reduce((s, m) => s + m.monatsstunden, 0);
    const monatsstundenGesamt = hoursFromStaff + n(calc.monatsstundenChef);
    const monatsstundenSalon = resolveSalonMonatsStunden(calc);
    const auslastungsziel = n(calc.auslastungsfaktor) || 75;
    const ruecklagenFaktor = resolveRuecklagenFaktor(calc);

    const steadyTotpunktKostendeckungNetto = fixSockel + unternehmerlohn + fullPersonal;
    const steadyTotpunktKostendeckungBrutto = steadyTotpunktKostendeckungNetto * mwstFaktor;

    /** Umsatzziel inkl. Rücklagen (+ Liquidität), analog Gesamtleistung */
    const steadyTotpunktNetto = steadyTotpunktKostendeckungNetto * (1 + ruecklagenFaktor);
    const steadyTotpunktBrutto = steadyTotpunktNetto * mwstFaktor;

    /**
     * Mindestumsatz pro Std. (Brutto) bei Auslastungsziel —
     * Formel wie PlanzieleSektion ruecklagen.mindestUmsatzBrutto75
     */
    const mindestUmsatzBrutto75 = safeDiv(
        safeDiv(steadyTotpunktNetto, monatsstundenSalon) * mwstFaktor,
        auslastungsziel / 100
    );
    const stundensatzErforderlich = mindestUmsatzBrutto75;

    const investitionenGesamt = totalInvestments(state.foundingType, state.investments);
    const foerderungenGesamt = totalSubsidies(state.subsidies);

    /** Anlauf-Liquiditätspuffer: Absicherung Fixkosten + Unternehmerlohn */
    const loan = state.loanPlan || createDefaultLoanPlan();
    const pufferMonate = (() => {
        const raw = loan.pufferMonate;
        if (raw === undefined || raw === null || !Number.isFinite(Number(raw))) {
            return ANLAUF_PUFFER_MONATE_FALLBACK;
        }
        return Math.min(6, Math.max(0, Number(raw)));
    })();
    const anlaufLiquiditaetspuffer = (fixSockel + unternehmerlohn) * pufferMonate;
    const nettoFinanzbedarf = Math.max(
        0,
        investitionenGesamt + anlaufLiquiditaetspuffer - foerderungenGesamt
    );
    const annuitaet = computeLoanAnnuitaet(
        nettoFinanzbedarf,
        loan.laufzeitJahre,
        loan.zinssatzProzent
    );
    const startFinanzbedarf = nettoFinanzbedarf;
    const rateMonat = annuitaet.rateMonat;

    const planumsatzNetto = n(calc.planumsatzGesamtNetto) || n(calc.monatsumsatzNetto);
    const planumsatzBrutto = planumsatzNetto * mwstFaktor;
    /** Nur explizit TAKEOVER = Vollbetrieb ab M1; alles andere = Anlaufkurve */
    const foundingType: FoundingType =
        state?.foundingType === 'TAKEOVER' ? 'TAKEOVER' : 'NEW';
    const isTakeover = foundingType === 'TAKEOVER';

    const verbesserungDeltaNetto = n(verbesserungUmsatzDeltaNetto);
    const verbesserungDeltaBrutto = verbesserungDeltaNetto * mwstFaktor;
    const hasVerbesserung = Math.abs(verbesserungDeltaBrutto) > 0.5;

    const prognoseModus: PEStartPrognoseModus = !isTakeover
        ? 'NEW_ANLAUF'
        : hasVerbesserung
          ? 'TAKEOVER_PLANZIEL_PLUS_VERBESSERUNG'
          : 'TAKEOVER_PLANZIEL';

    const months: PEStartMonthRow[] = [];
    let liquiditaet = 0;

    for (let month = 1; month <= 6; month++) {
        const activeStaff = staff.filter((m) => {
            const flag = resolveFlag(state.staffFlags, m.id, foundingType);
            return flag.startMonth <= month;
        });
        const personalkosten = activeStaff.reduce((s, m) => s + m.totalCost, 0);
        const totpunktKostendeckungNetto = fixSockel + unternehmerlohn + personalkosten;
        const totpunktMitRuecklagenNetto = totpunktKostendeckungNetto * (1 + ruecklagenFaktor);
        const umsatzZiel = totpunktMitRuecklagenNetto * mwstFaktor;

        /**
         * NEW: lineare Anlaufkurve auf (Planziel + volle Verbesserung) bis M6.
         * TAKEOVER: ab M1 = Planziel; geplante Verbesserungen (Planungsanalyse)
         *           moderat über Monat/6 einarbeiten — keine Neugründungs-Rampe.
         */
        const improveFactor = month / 6;
        let umsatzPrognose: number;
        let anlaufAuslastung: number;
        let verbesserungAnteilBrutto: number;

        if (isTakeover) {
            verbesserungAnteilBrutto = verbesserungDeltaBrutto * improveFactor;
            umsatzPrognose = planumsatzBrutto + verbesserungAnteilBrutto;
            anlaufAuslastung = auslastungsziel;
        } else {
            const zielUmsatzBrutto = planumsatzBrutto + verbesserungDeltaBrutto;
            umsatzPrognose = zielUmsatzBrutto * improveFactor;
            verbesserungAnteilBrutto = verbesserungDeltaBrutto * improveFactor;
            anlaufAuslastung = auslastungsziel * improveFactor;
        }

        const foerderung = subsidyForMonth(state.subsidies, month);
        const investition = month === 1 ? investitionenGesamt : 0;
        const pufferAbfluss = month === 1 ? anlaufLiquiditaetspuffer : 0;
        const kreditAuszahlung = month === 1 ? nettoFinanzbedarf : 0;
        const kreditrate = rateMonat;

        liquiditaet +=
            umsatzPrognose +
            foerderung +
            kreditAuszahlung -
            fixSockel -
            unternehmerlohn -
            personalkosten -
            investition -
            pufferAbfluss -
            kreditrate;

        months.push({
            month,
            umsatzZiel,
            umsatzPrognose,
            anlaufAuslastung,
            verbesserungAnteilBrutto,
            fixkosten: fixSockel,
            personalkosten,
            unternehmerlohn,
            foerderung,
            investition,
            kreditAuszahlung,
            kreditrate,
            liquiditaetssaldo: liquiditaet,
            decktTotpunkt: umsatzPrognose >= umsatzZiel && umsatzZiel > 0,
        });
    }

    let breakEvenMonat: number | null = null;
    for (let i = 0; i < months.length; i++) {
        const fromHere = months.slice(i);
        if (fromHere.length > 0 && fromHere.every((row) => row.decktTotpunkt)) {
            breakEvenMonat = months[i].month;
            break;
        }
    }

    return {
        foundingType,
        prognoseModus,
        verbesserungUmsatzDeltaNetto: verbesserungDeltaNetto,
        kpis: {
            totpunktKostendeckungNetto: steadyTotpunktKostendeckungNetto,
            totpunktKostendeckungBrutto: steadyTotpunktKostendeckungBrutto,
            totpunktMonatNetto: steadyTotpunktNetto,
            totpunktMonatBrutto: steadyTotpunktBrutto,
            ruecklagenFaktor,
            unternehmerlohnKompass: unternehmerlohn,
            unternehmerlohnKompassRounded: unternehmerlohnRounded,
            unternehmerlohnSource: ul.source,
            planumsatzNetto,
            stundensatzErforderlich,
            mindestUmsatzBrutto75,
            auslastungsziel,
            startFinanzbedarf,
            investitionenGesamt,
            foerderungenGesamt,
            monatsstundenGesamt,
            monatsstundenSalon,
            finanzbedarf: {
                investitionen: investitionenGesamt,
                anlaufLiquiditaetspuffer,
                anlaufPufferMonate: pufferMonate,
                foerderungen: foerderungenGesamt,
                nettoFinanzbedarf,
                kreditbetrag: annuitaet.kreditbetrag,
                tilgungLaufzeitJahre: annuitaet.laufzeitJahre,
                zinssatzProzent: annuitaet.zinssatzProzent,
                rateMonat: annuitaet.rateMonat,
                tilgungMonat: annuitaet.rateMonat,
                zinsAnteilMonat1: annuitaet.zinsAnteilMonat1,
                tilgungAnteilMonat1: annuitaet.tilgungAnteilMonat1,
                gesamtzahlung: annuitaet.gesamtzahlung,
                gesamtzins: annuitaet.gesamtzins,
            },
        },
        months,
        breakEvenMonat,
        currencyCode,
        mwstSatz: mwst,
        formatCurrency,
    };
}
